import {
  useUpdateExamRoomMutation,
  AdminGetExamRoomByIdDocument,
  GetExamRoomByIdDocument,
} from '@exam-center/graphql';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Form,
  FormButton,
  FormTextField,
  ReadonlyField,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { useAdminExamLocationDetail } from '../../providers/admin-exam-location-detail-provider';

const UpdateRoomSchema = z.object({
  name: z.string().min(1),
});
type UpdateRoomFormInput = z.infer<typeof UpdateRoomSchema>;

interface AdminExamLocationDetailsUpdateRoomModalProps {
  open: boolean;
  onClose: () => void;
}

export default function AdminExamLocationDetailsUpdateRoomModal(
  props: Readonly<AdminExamLocationDetailsUpdateRoomModalProps>,
) {
  const { open: isOpen, onClose } = props;

  const { examRoomInfo } = useAdminExamLocationDetail();

  const { showSnackbar } = useSnackbar();

  const formContext = useForm<UpdateRoomFormInput>({
    defaultValues: {
      name: examRoomInfo.name,
    },
    resolver: zodResolver(UpdateRoomSchema),
  });

  const [updateExamRoom] = useUpdateExamRoomMutation();

  const {
    formState: { isSubmitting },
  } = formContext;

  const handleUpdateRoom = async (data: UpdateRoomFormInput) => {
    const { name } = data;

    await updateExamRoom({
      variables: {
        updateExamRoomInput: {
          id: examRoomInfo.id,
          name,
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: 'บันทึกข้อมูลเรียบร้อยแล้ว',
          variant: 'success',
        });

        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'ไม่สามารถบันทึกข้อมูลได้',
          variant: 'error',
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [AdminGetExamRoomByIdDocument, GetExamRoomByIdDocument],
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formContext.handleSubmit(handleUpdateRoom)();
  };

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='lg'
        slotProps={{
          paper: { component: Form, onSubmit: handleSubmit },
          transition: { onExited: () => formContext.reset() },
        }}
      >
        <DialogTitle onClose={onClose} loading={isSubmitting}>
          แก้ไขข้อมูลห้องสอบ
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Flex
            sx={(theme) => ({
              flexDirection: 'column',
              gap: theme.spacing(4),
            })}
          >
            <ReadonlyField label='ชั้น' content={examRoomInfo.floor} />
            <FormTextField name='name' label='ชื่อห้อง' placeholder='กรุณากรอกชื่อห้อง' required />
            <ReadonlyField label='เลขที่ห้อง' content={examRoomInfo.roomNumber ?? ''} />
            <ReadonlyField label='จำนวนที่นั่ง' content={`${examRoomInfo.seat}`} />
          </Flex>
        </DialogContent>
        <Divider />
        <DialogActions>
          <Button
            size='large'
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disabled={isSubmitting}
          >
            ยกเลิก
          </Button>
          <FormButton label='บันทึก' size='large' />
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
